import React, { useState, useCallback } from "react"
import PropTypes from "prop-types"
import { Button, Tag, Input, Space, Spin, Flex, Modal, Typography, Grid, Drawer } from "antd"
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons"
import { useDispatch } from "react-redux"
import { createGroup, removeGroup, updateGroup } from "store/clients"
import { getGroups } from "store/groups"
import { serialize } from "object-to-formdata"
import { GROUP_COLORS } from "constants/clients"
import styles from "./index.module.css"
import { Text } from "ui/Typography"
import warningIcon from "assets/icons/warningIcon.svg"

const GroupMenuItems = ({
  action = "",
  groups = [],
  client,
  selectedClientIds,
  selectedGroups = [],
  onSelectedGroupsChange,
  moduleName,
  setSelectedClientIds
}) => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState(null)
  const [newName, setNewName] = useState("")
  const [selectedColor, setSelectedColor] = useState("")
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [groupToDelete, setGroupToDelete] = useState(null)
  const [addGroupValidation, setAddGroupValidation] = useState({
    name: false,
    color: false
  })

  const handleSelectGroup = useCallback(
    async (e, group) => {
      e.stopPropagation()
      const arr = selectedGroups?.flatMap((g) => g.group_id ?? g.id) || []
      if (arr.includes(group.id)) return
      const groupClone = { ...group }
      groupClone.group_id = groupClone?.id
      if (client?.id) {
        setSelectedClientIds?.([client.id])
        onSelectedGroupsChange?.([...selectedGroups, groupClone])
      } else if (selectedClientIds?.length > 0) {
        onSelectedGroupsChange?.([...selectedGroups, groupClone])
      } else if (!selectedGroups.includes(group.id)) {
        onSelectedGroupsChange?.([...selectedGroups, groupClone])
      }
    },
    [selectedGroups, client?.id, selectedClientIds, setSelectedClientIds, onSelectedGroupsChange]
  )

  const handleAddClick = (e) => {
    e.stopPropagation()
    setAdding(true)
  }

  const handleCancelEdit = (e) => {
    e.stopPropagation()
    setAdding(false)
    setEditing(null)
    setNewName("")
    setSelectedColor("")
  }

  const handleSaveGroup = useCallback(
    async (e) => {
      e?.stopPropagation()
      setAddGroupValidation({
        name: false,
        color: false
      })
      if (!newName.trim() || !selectedColor) {
        setAddGroupValidation({
          name: !newName.trim(),
          color: !selectedColor
        })
        return
      }

      setLoading(true)
      try {
        if (editing) {
          const updatedGroup = { name: newName, color: selectedColor }
          await dispatch(updateGroup(editing.id, { group: updatedGroup }))
        } else {
          const formData = serialize({
            group: {
              name: newName,
              color: selectedColor
            }
          })
          await dispatch(createGroup(formData))
        }
        await dispatch(getGroups())
        handleCancelEdit(e)
      } catch (err) {
        console.error("Failed to save group", err)
      } finally {
        setLoading(false)
      }
    },
    [editing, newName, selectedColor, dispatch]
  )

  const handleEditClick = (e, group) => {
    e.stopPropagation()
    setEditing(group)
    setNewName(group.name)
    setSelectedColor(group.color)
    setAdding(true)
  }

  const handleDeleteClick = (e, group) => {
    e.stopPropagation()
    setGroupToDelete(group)
    setDeleteModalVisible(true)
  }
  const handleDeleteConfirm = useCallback(async () => {
    if (!groupToDelete) return

    setLoading(true)
    try {
      await dispatch(removeGroup(groupToDelete.id))
      await dispatch(getGroups())

      // Remove from selected groups if needed
      if (selectedGroups.includes(groupToDelete.id)) {
        const updated = selectedGroups.filter((id) => id.id !== groupToDelete.id)
        onSelectedGroupsChange?.(updated)
      }

      setDeleteModalVisible(false)
      setGroupToDelete(null)
    } catch (err) {
      console.error("Failed to delete group", err)
    } finally {
      setLoading(false)
    }
  }, [groupToDelete, dispatch, selectedGroups, onSelectedGroupsChange])
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const editGroupForm = (
    <Space direction="vertical" className={styles.groupWrapper}>
      <Text className="fw-500">Group Name</Text>
      <Input placeholder="Group name" value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus />
      <Text className="fw-500">Color</Text>
      <Space wrap className={styles.colorTag} size={[0, 0]}>
        {GROUP_COLORS.map((color) => (
          <button
            key={color}
            onClick={(e) => {
              e.stopPropagation()
              setSelectedColor(color)
            }}
            className={`px-xxs  align-center justify-center  ${selectedColor === color ? styles.selected : ""}`}
            style={{ border: "none", background: "none" }}
          >
            <Tag
              color={color}
              className={styles.groupColorTag}
              style={{
                outline: selectedColor === color ? `2px solid #1D72F4` : "none"
              }}
            />
          </button>
        ))}
      </Space>
      <Flex gap={8}>
        <Button onClick={handleCancelEdit}>Cancel</Button>
        <Button type="primary" onClick={handleSaveGroup}>
          Update
        </Button>
      </Flex>
    </Space>
  )

  const groupForm = (
    <Space direction="vertical" className="w-full p-xs">
      <Text className="fw-500">Group Name</Text>
      <Input
        placeholder="Group name"
        value={newName}
        onChange={(e) => setNewName(e.target.value)}
        autoFocus
      />
      <Space wrap className={styles.colorTag} size={[0, 0]}>
        {GROUP_COLORS.map((color) => (
          <button
            key={color}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedColor(color);
            }}
            className={selectedColor === color ? styles.selected : ""}
            style={{ border: "none", background: "none" }}
          >
            <Tag
              color={color}
              style={{
                outline: selectedColor === color ? `2px solid #1D72F4` : "none"
              }}
              className={styles.groupColorTag}
            />
          </button>
        ))}
      </Space>
      <Flex gap={8}>
        <Button onClick={handleCancelEdit}>Cancel</Button>
        <Button type="primary" onClick={handleSaveGroup}>Save</Button>
      </Flex>
      {(addGroupValidation.name || addGroupValidation.color) && (
        <Text type="danger">Group name and color both are required</Text>
      )}
    </Space>
  );

  return (
    <>
      <div className={styles.customDropdown}>
        <div className={`${styles.groupItems} ${moduleName}`}>
          {groups?.filter(Boolean).map((group) => {
            const isSelected = selectedGroups.some((selectedGroup) => selectedGroup?.group_id === group?.id)
            return (
              <React.Fragment key={group?.id}>
                <button
                  onClick={(e) => handleSelectGroup(e, group)}
                  className={`w-full ${styles.groupButton} ${isSelected ? styles.disabledGroupButton : ""}`}
                  disabled={isSelected}
                >
                  <Tag
                    color={group.color}
                    style={{
                      color: group.color,
                      backgroundColor: group.color + "26",
                      opacity: isSelected ? 0.5 : 1,
                      cursor: isSelected ? "not-allowed" : "pointer"
                    }}
                    className={styles.groupTag}
                  >
                    {group.name}
                  </Tag>
                  {action !== "bulk_add" && (
                    <Space size={15}>
                      <DeleteOutlined color="#525455" onClick={(e) => handleDeleteClick(e, group)} />
                      <EditOutlined color="#525455" onClick={(e) => handleEditClick(e, group)} />
                    </Space>
                  )}
                </button>
                {editing && editing.id === group.id && (
                  isMobile ? (
            <Drawer
              open={true}
              placement="bottom"
              closable={false}
              height="auto"
              onClose={handleCancelEdit}
              bodyStyle={{ padding: 16 }}
            >
              {editGroupForm}
            </Drawer>
          ) : (
            editGroupForm
          )
                )}
              </React.Fragment>
            )
          })}
        </div>
        {!editing && adding && (
          isMobile ? (
            <Drawer
              open={true}
              placement="bottom"
              closable={false}
              height="auto"
              onClose={handleCancelEdit}
              bodyStyle={{ padding: 16 }}
            >
              {groupForm}
            </Drawer>
          ) : (
            groupForm
          )
        )}

        {!adding && (
          <Button color="primary" variant="text" onClick={handleAddClick} className="align-center mt-xs gap-xxs">
            <PlusOutlined />
            <span>Add New Group</span>
          </Button>
        )}

        {loading && <Spin className="mt-xs" />}
      </div>

      <Modal
        centered
        open={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        width={360}
        footer={null}
        title={
          <div>
            <Flex vertical justify="center" align="center" className={`mb-m ${styles.warningIcon}`}>
              <img src={warningIcon} alt="Warning Icon" width={24} height={24} />
            </Flex>
          </div>
        }
      >
        <h3 className="text-left">Delete Group?</h3>
        <Typography.Text className="mb-xl block text-left">
          Are you sure you want to delete group <b>[{groupToDelete?.name}]</b>?
        </Typography.Text>

        <Flex justify="left" gap={10} className={styles.formModalFooter}>
          <Button onClick={() => setDeleteModalVisible(false)}>Cancel</Button>
          <Button danger type="primary" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </Flex>
      </Modal>
    </>
  )
}

GroupMenuItems.propTypes = {
  action: PropTypes.string,
  groups: PropTypes.arrayOf(PropTypes.object).isRequired,
  client: PropTypes.object,
  selectedClientIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  selectedGroups: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  onSelectedGroupsChange: PropTypes.func,
  setSelectedClientIds: PropTypes.func,
  moduleName: PropTypes.string
}

export default GroupMenuItems
